import { SxProps, Theme } from '@mui/material';
import { SlideObject } from '../types';
import { resolvedTextPadding } from './shapeFrame';
import { sanitizePresentationHtml } from './textSanitize';

const inheritTypeSx = {
  margin: 0,
  padding: 0,
  lineHeight: 'inherit',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  letterSpacing: 'inherit',
  color: 'inherit',
};

export const textFlowSx: SxProps<Theme> = {
  whiteSpace: 'pre-wrap',
  '& p, & h1, & h2, & h3, & li, & li p, & ul, & ol, & span': inheritTypeSx,
  '& strong, & b, & strong *, & b *': { fontWeight: '700 !important' },
  '& em, & i, & em *, & i *': { fontStyle: 'italic !important' },
  '& u, & u *': { textDecoration: 'underline !important' },
  '& ul, & ol, & .ProseMirror ul, & .ProseMirror ol': {
    display: 'block',
    margin: 0,
    padding: '0 0 0 1.35em',
    listStylePosition: 'outside',
  },
  '& ul': { listStyleType: 'disc' },
  '& ol': { listStyleType: 'decimal' },
  '& li, & li p': { display: 'list-item' },
  '& .ProseMirror': {
    outline: 'none',
    width: '100%',
    margin: 0,
    padding: 0,
    lineHeight: 'inherit',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    letterSpacing: 'inherit',
    color: 'inherit',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    cursor: 'text',
    userSelect: 'text',
    caretColor: '#0A2757',
  },
};

export function resolveMediaUrl(url?: string): string {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) return url;
  const path = url.startsWith('/') ? url : `/${url}`;
  // Same-origin so editor, presenter and PDF capture share one image path.
  // Dev: CRA proxy. Production: the app serves /uploads.
  return path;
}

export function objectSurfaceSx(object: SlideObject) {
  if (object.type !== 'text') return {};
  const radius = object.style?.cornerRadius || 0;
  const pad = resolvedTextPadding(object);
  const bg = object.style?.backgroundColor;
  return {
    boxSizing: 'border-box' as const,
    padding: pad ? `${pad}px` : undefined,
    bgcolor: bg && bg !== 'transparent' ? bg : 'transparent',
    borderRadius: radius || undefined,
  };
}

export function mediaRadiusSx(object: SlideObject) {
  const radius = object.style?.cornerRadius || 0;
  return radius > 0 ? { borderRadius: `${radius}px` } : {};
}

export function objectPreviewHtml(object: SlideObject): string {
  if (object.type === 'text' || (object.type === 'shape' && object.content)) return sanitizePresentationHtml(object.content);
  if (object.type === 'image' || object.type === 'product') {
    const src = resolveMediaUrl(object.imageUrl);
    if (!src) return '';
    return `<img src="${src}" alt="" style="width:100%;height:100%;object-fit:${object.style?.objectFit || 'contain'};" />`;
  }
  return '';
}
